import {useEditor} from "@grapesjs/react";
import {capitalize, isString} from "../../../app/editor/utils";
import cx from '../../../app/editor/utils/makeCls';
import Button from "../Button";
import CodeField from "../CodeField";
import ColorField from "../ColorField";
import Grid from "../Grid";
import GridItem from "../GridItem";
import InputField from '../InputField';
import NumberField from "../NumberField";
import SelectField from "../SelectField";
import SwitchField from "../SwitchField";
import TraitStackField from "./TraitStackField";
import TraitHrefField from "./TraitHrefField";
import styled from "styled-components";
import {useState} from "react";
import "../../../styles/custom-style.scss"
import TraitVideoSrcField from "./TraitVideoSrcField";

const cleanLabel = (label) => capitalize(label.replace(/-/g, ' '))

export const getInputProps = (trait, editor) => {
    return {
        type: trait.getType(),
        size: 's',
        value: trait.getValue(),
        label: cleanLabel(trait.getLabel()),
        placeholder: editor?.t(`traitManager.traits.attributes.${trait.getId()}.placeholder`),
        onChange: (value, partial) => trait.setValue(value, {partial}),
    }
}

export const getSelectProps = (trait) => {
    return {
        ...getInputProps(trait),
        options: trait.getOptions().map((opt) => {
            return isString(opt) ?
                {id: opt, label: opt} :
                {
                    id: opt.id || opt.value,
                    label: opt.name || opt.label || opt.value,
                }
        }),
        onChange: (value) => trait.setValue(value),
    }
}

export const getNumberProps = (trait) => {
    return {
        ...getInputProps(trait),
        units: trait.get('units'),
    }
}

export const getButtonProps = (trait) => {
    return {
        children: cleanLabel(trait.getLabel() || trait.get('text')),
        onClick: () => {
            const editor = trait.em.Editor;
            const command = trait.get('command');
            isString(command) ? editor.runCommand(command) : command?.(editor, trait);
        },
        full: true,
    }
}

export const getCheckboxProps = (trait) => {
    return {
        size: 's',
        value: !!trait.getValue(),
        label: cleanLabel(trait.getLabel()),
        onChange: (value) => trait.setValue(value),
    }
}

export const getCodeProps = (trait, editor) => {
    const typeProps = trait.get('typeProps') || {};
    return {
        language: 'css',
        ...typeProps,
        key: editor?.getSelected()?.getId() || '',
        value: trait.getValue(),
        label: cleanLabel(trait.getLabel()),
        onChange: (value, partial) => {
            trait.setValue(value);
        },
    }
}

function getTraitComponent(trait, editor) {
    const selectedComponent = editor.getSelected();
    if (selectedComponent.attributes.tagName === 'video') {
        return <TraitVideoSrcField trait={trait}/>;
    }
    switch (trait.getType()) {
        case 'select':
            return <SelectField {...getSelectProps(trait)}/>
        case 'number':
            return <NumberField {...getNumberProps(trait)}/>
        case 'checkbox':
            return <SwitchField {...getCheckboxProps(trait)}/>
        case 'color':
            return <ColorField {...getInputProps(trait)}/>
        case 'button':
            return <Button {...getButtonProps(trait)}/>
        case 'code':
            return <CodeField {...getCodeProps(trait, editor)}/>
        case 'stack':
            return <TraitStackField trait={trait}/>
        case 'href':
            return <TraitHrefField trait={trait}/>
        default:
            return <InputField {...getInputProps(trait, editor)}/>
    }
}

export default function TraitManager({traits}) {
    const [ViewToggle, isSetViewToggle] = useState('');
    const editor = useEditor();
    const InnerView = (e) => {
        ViewToggle === "" ? isSetViewToggle("active") : isSetViewToggle("");
    };
    const convertTraits = [];
    for (const trait of traits) {
        if (["id", "title", "alt", "loading"].includes(trait.id)) continue;
        if (trait.attributes.name === "target") {
            trait.setValue(true);
        } else {
            convertTraits.push(trait);
        }
    }
    traits = convertTraits;
    return (
        <Grid className="app-trait-manager" col>
            {
                !traits.length ?
                    <GridItem className="text-center view-none">{"변경할 영역을 선택해 주세요"}</GridItem>
                    :

                    <Trant className={ViewToggle}>
                        <TrantHead
                            onClick={InnerView}
                        >
                            <p>Properties</p>
                            <svg viewBox="0 0 24 24" role="presentation"
                                 className="w-5 h-5 transition-transform propertie-arrow">
                                <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"></path>
                            </svg>
                        </TrantHead>
                        <TrantInner className={'inner-trait'}>
                            {
                                traits.map((trait) => (
                                    <GridItem key={trait.getId()} className={cx('mb-2 w-full')}>
                                        {getTraitComponent(trait, editor)}
                                    </GridItem>
                                ))
                            }
                        </TrantInner>
                    </Trant>
            }
        </Grid>
    );
}
const Trant = styled.div`
    &.active {
        .inner-trait {
            display: block;
        }

        .propertie-arrow {
            transform: rotate(180deg);
        }
    }
`;
const TrantHead = styled.div`
    background-color: #27272a;
    color: rgb(226 232 240 / 0.8);
    padding: 0.25rem 0.5rem;
    border-bottom: 1px solid rgb(226 232 240 / 0.25);
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;

    .propertie-arrow {
        fill: currentcolor;
        transition-property: transform;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 150ms;
    }
`;
const TrantInner = styled.div`
    padding: 0.25rem 0.5rem;
    display: none;
`;